/**
 * Guest cases: labels, routes, time formatting and evidence grouping.
 * Pure functions, no I/O, shared by the case pages and the calendar entry points.
 */

import type {
  CaseCategory,
  CaseEventType,
  CaseEvidence,
  CaseOutcome,
  CaseStatus,
  CaseStayOption,
  ClientCaseEventType,
  EvidenceSnapshot,
  EvidenceType,
  GuestCaseDetail,
  StayHistoryEntry,
} from '@/types/guest-cases';
import { formatDate, nightsBetween } from '@/utils/dates';

// ─── Routes ───────────────────────────────────────────────────────────────────

export const CASES_PATH = '/app/cases';
/** Query parameter that opens the new-case form with a stay chosen (the calendar entry point uses it). */
export const STAY_QUERY_PARAM = 'stay';
export const STATUS_QUERY_PARAM = 'status';

export function casePath(caseId: number): string {
  return `${CASES_PATH}/${caseId}`;
}

export function newCasePathForStay(stayId: number): string {
  return `${CASES_PATH}?${STAY_QUERY_PARAM}=${stayId}`;
}

/** How far back the stay picker looks when opening a case. */
export const STAY_LOOKBACK_DAYS = 90;

// ─── Labels (keys match the backend enums exactly) ────────────────────────────

export const CASE_CATEGORY_LABELS: Record<CaseCategory, string> = {
  refund_request: 'Refund request',
  odor: 'Odor or smoke',
  cleanliness: 'Cleanliness',
  damage: 'Damage',
  amenity: 'Amenity problem',
  noise: 'Noise',
  access: 'Access or check-in',
  review_threat: 'Review threat',
  other: 'Other',
};
export const CASE_CATEGORIES = Object.keys(CASE_CATEGORY_LABELS) as CaseCategory[];

export const CASE_STATUS_LABELS: Record<CaseStatus, string> = {
  open: 'Open',
  awaiting_platform: 'Awaiting platform',
  resolved: 'Resolved',
  closed: 'Closed',
};
export const CASE_STATUSES = Object.keys(CASE_STATUS_LABELS) as CaseStatus[];

/** Badge classes per status. The badge always shows the label too, so color is never the only signal. */
export const CASE_STATUS_BADGE_CLASSES: Record<CaseStatus, string> = {
  open: 'badge-pending dark:bg-yellow-900/60 dark:text-yellow-100',
  awaiting_platform: 'badge-in-progress dark:bg-blue-900/60 dark:text-blue-100',
  resolved: 'badge-completed dark:bg-green-900/60 dark:text-green-100',
  closed: 'badge bg-gray-100 text-gray-800',
};

export const CASE_OUTCOME_LABELS: Record<CaseOutcome, string> = {
  no_refund: 'No refund',
  partial_refund: 'Partial refund',
  full_refund: 'Full refund',
  withdrawn: 'Guest withdrew the claim',
};
export const CASE_OUTCOMES = Object.keys(CASE_OUTCOME_LABELS) as CaseOutcome[];

/** Outcomes where money went back to the guest, so the form asks for the amount. */
export const REFUND_OUTCOMES: readonly CaseOutcome[] = ['partial_refund', 'full_refund'];

/** The status whose save collects an outcome and refund amount. */
export const RESOLVED_STATUS: CaseStatus = 'resolved';

export const CASE_EVENT_TYPE_LABELS: Record<CaseEventType, string> = {
  guest_report: 'Guest report',
  host_response: 'Your response',
  platform_contact: 'Platform contact',
  platform_decision: 'Platform decision',
  concession: 'Concession offered',
  note: 'Note',
  checkout_check: 'Checkout check',
  status_change: 'Status change',
};

/**
 * Event types the timeline form offers. status_change is written by the
 * server; checkout_check is left to the checkout check form, which also saves
 * the check itself as evidence.
 */
export const TIMELINE_FORM_EVENT_TYPES = (Object.keys(CASE_EVENT_TYPE_LABELS) as CaseEventType[])
  .filter((type): type is ClientCaseEventType => type !== 'status_change' && type !== 'checkout_check');

export const EVIDENCE_TYPE_LABELS: Record<EvidenceType, string> = {
  turnover_media: 'Turnover photo or video',
  inspection_photo: 'Inspection photo',
  checkout_check: 'Checkout check',
  lock_event: 'Lock event',
  noise_event: 'Noise event',
  checkin_agreement: 'Check-in agreement',
  airbnb_thread: 'Airbnb messages',
  vrbo_interaction: 'VRBO messages',
  booking_com_interaction: 'Booking.com messages',
};

/** Evidence the database protects from deletion while it is pinned to a case. */
export const DELETE_PROTECTED_EVIDENCE: readonly EvidenceType[] = ['turnover_media', 'inspection_photo', 'checkout_check'];

const PLATFORM_LABELS: Record<string, string> = {
  airbnb: 'Airbnb',
  vrbo: 'VRBO',
  booking: 'Booking.com',
  booking_com: 'Booking.com',
  'booking.com': 'Booking.com',
  direct: 'Direct',
};

export function platformLabel(source: string | null | undefined): string | null {
  if (!source) return null;
  const known = PLATFORM_LABELS[source.toLowerCase()];
  return known ?? source.charAt(0).toUpperCase() + source.slice(1);
}

// ─── Snapshot fields ──────────────────────────────────────────────────────────

export function snapshotString(snapshot: EvidenceSnapshot | null | undefined, key: string): string | null {
  const value = snapshot?.[key];
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

export function snapshotNumber(snapshot: EvidenceSnapshot | null | undefined, key: string): number | null {
  const value = snapshot?.[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** A 0/1 column copied into a snapshot: true, false, or null when absent. */
export function snapshotFlag(snapshot: EvidenceSnapshot | null | undefined, key: string): boolean | null {
  const value = snapshot?.[key];
  if (value === 1 || value === true) return true;
  if (value === 0 || value === false) return false;
  return null;
}

// ─── Times ────────────────────────────────────────────────────────────────────

const SQLITE_UTC_DATETIME = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
const EXPLICIT_ZONE = /(?:Z|[+-]\d{2}:?\d{2})$/i;
const WALL_CLOCK = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/;
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A server timestamp as an instant: ISO 8601 with a zone, or SQLite's UTC text
 * ('2026-09-20 15:15:00'). null for anything without a zone, which is not an
 * instant at all.
 */
export function parseServerTime(value: string | null | undefined): Date | null {
  if (!value) return null;
  const iso = SQLITE_UTC_DATETIME.test(value) ? `${value.replace(' ', 'T')}Z` : value;
  if (!EXPLICIT_ZONE.test(iso)) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

const MINUTE_FORMAT: Intl.DateTimeFormatOptions = {
  month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
};
const SECOND_FORMAT: Intl.DateTimeFormatOptions = { ...MINUTE_FORMAT, second: '2-digit' };

/** "Sep 19, 2026, 11:20 AM EDT": the instant in the viewer's time zone, with the zone named. */
export function formatInstant(date: Date, precision: 'minute' | 'second' = 'minute'): string {
  const options = precision === 'second' ? SECOND_FORMAT : MINUTE_FORMAT;
  return date.toLocaleString('en-US', { ...options, timeZoneName: 'short' });
}

export function formatServerTime(value: string | null | undefined, precision: 'minute' | 'second' = 'minute'): string | null {
  const date = parseServerTime(value);
  return date ? formatInstant(date, precision) : null;
}

/** The viewer's time zone as a short name ("EDT"), for labelling date and time inputs. */
export function localZoneName(at: Date = new Date()): string {
  const part = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' }).formatToParts(at)
    .find((p) => p.type === 'timeZoneName');
  return part?.value ?? 'local time';
}

export interface CapturedTime {
  text: string;
  /** False when the file recorded its device's clock reading with no time zone. */
  zoneKnown: boolean;
}

/**
 * A capture time from a file's own metadata. With an offset it is an instant
 * and gets the viewer's zone; without one it is the device's clock reading,
 * shown as recorded, because converting it would invent a time zone.
 */
export function formatCapturedTime(value: string | null | undefined): CapturedTime | null {
  if (!value) return null;
  if (EXPLICIT_ZONE.test(value)) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : { text: formatInstant(date, 'second'), zoneKnown: true };
  }
  const match = WALL_CLOCK.exec(value);
  if (!match) return null;
  const [, year, month, day, hour, minute, second = '00'] = match;
  const reading = new Date(Date.UTC(+year, +month - 1, +day, +hour, +minute, +second));
  if (Number.isNaN(reading.getTime())) return null;
  return { text: reading.toLocaleString('en-US', { ...SECOND_FORMAT, timeZone: 'UTC' }), zoneKnown: false };
}

const pad2 = (n: number): string => String(n).padStart(2, '0');

/** A Date as a datetime-local input value ('2026-09-19T11:30') in the viewer's time zone. */
export function toDateTimeLocal(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

/** A datetime-local value, read in the viewer's time zone as the input shows it; null when unusable. */
export function dateTimeLocalToDate(value: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?$/.test(value)) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** The viewer's calendar date of an instant, as YYYY-MM-DD. */
export function localDateOf(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

/** YYYY-MM-DD shifted by whole days, computed at noon UTC so it never crosses a date line. */
export function shiftDays(day: string, days: number): string {
  const date = new Date(`${day}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/** "Sep 18", or "Sep 18, 2025" outside the current year: the day of an instant, in the viewer's zone. */
export function formatDayOfInstant(date: Date, now: Date = new Date()): string {
  const sameYear = date.getFullYear() === now.getFullYear();
  return date.toLocaleDateString('en-US', sameYear
    ? { month: 'short', day: 'numeric' }
    : { month: 'short', day: 'numeric', year: 'numeric' });
}

/** "Sep 17 – 19, 2026", "Sep 30 – Oct 2, 2026" or "Dec 30, 2026 – Jan 2, 2027". */
export function formatStayRange(checkIn: string | null, checkOut: string | null): string {
  if (!checkIn || !checkOut) return 'Dates not recorded';
  const [inYear, inMonth] = checkIn.split('-');
  const [outYear, outMonth] = checkOut.split('-');
  const full: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
  if (inYear !== outYear) return `${formatDate(checkIn, full)} – ${formatDate(checkOut, full)}`;
  const start = formatDate(checkIn, { month: 'short', day: 'numeric' });
  // Built by hand: Intl renders { day, year } alone as "2026 (day: 19)".
  if (inMonth === outMonth) return `${start} – ${formatDate(checkOut, { day: 'numeric' })}, ${outYear}`;
  return `${start} – ${formatDate(checkOut, full)}`;
}

export function formatNights(nights: number): string {
  return `${nights} ${nights === 1 ? 'night' : 'nights'}`;
}

export function stayNights(checkIn: string | null, checkOut: string | null): number | null {
  if (!checkIn || !checkOut || !DATE_ONLY.test(checkIn) || !DATE_ONLY.test(checkOut)) return null;
  return nightsBetween(checkIn, checkOut);
}

// ─── Money and fingerprints ───────────────────────────────────────────────────

export function formatCents(cents: number): string {
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

export const FINGERPRINT_SHORT_LENGTH = 8;

export function shortFingerprint(sha256: string): string {
  return sha256.slice(0, FINGERPRINT_SHORT_LENGTH);
}

// ─── Stays ────────────────────────────────────────────────────────────────────

// Mirrors confirmationCodeFor in functions/utils/guest-cases.ts: OTA codes
// (HMRTPEFT8S, HA-1234567) versus iCal UIDs and our own writers' ids.
const OTA_CONFIRMATION_CODE = /^[A-Za-z0-9-]{6,16}$/;
const INTERNAL_ID_PREFIXES = ['blocked-', 'recurrence:', 'bulk-block:', 'guest-booking-'];

export function confirmationCodeOf(externalId: string | null | undefined): string | null {
  const id = externalId?.trim() ?? '';
  if (!id || !OTA_CONFIRMATION_CODE.test(id)) return null;
  return INTERNAL_ID_PREFIXES.some((prefix) => id.startsWith(prefix)) ? null : id;
}

export const BLOCKED_STATUS = 'blocked';
export const CANCELLED_STATUS = 'cancelled';

/** Whether the picker's search text matches a stay's guest, title, confirmation code or property. */
export function matchesStayQuery(stay: CaseStayOption, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return [stay.guest_name, stay.title, stay.external_id, stay.property_name]
    .some((field) => field?.toLowerCase().includes(needle));
}

export function stayGuestLabel(stay: Pick<CaseStayOption, 'guest_name' | 'title'>): string {
  return stay.guest_name?.trim() || stay.title?.trim() || 'Guest name not recorded';
}

// ─── Evidence groups ──────────────────────────────────────────────────────────

export type EvidenceGroupId = 'before_check_in' | 'after_checkout' | 'checkout_checks' | 'logs' | 'messages';

export interface EvidenceGroup {
  id: EvidenceGroupId;
  title: string;
  empty: string;
}

export const EVIDENCE_GROUPS: readonly EvidenceGroup[] = [
  { id: 'before_check_in', title: 'Before check-in', empty: 'No photos or videos from before check-in are pinned.' },
  { id: 'after_checkout', title: 'After checkout', empty: 'No photos or videos from after checkout are pinned.' },
  { id: 'checkout_checks', title: 'Checkout checks', empty: 'No checkout checks are pinned. Log one below.' },
  { id: 'logs', title: 'Logs', empty: 'No lock, noise or check-in records are pinned.' },
  { id: 'messages', title: 'Messages', empty: 'No message threads are pinned.' },
];

function dateOnly(value: string | null): string | null {
  const day = value?.slice(0, 10) ?? null;
  return day && DATE_ONLY.test(day) ? day : null;
}

/**
 * Which group a pin belongs in. Turnover media carries its relation to the
 * stay; media from another turnover, and inspection photos, are placed by
 * their date against check-in (on or before check-in counts as before).
 */
export function evidenceGroupOf(
  item: Pick<CaseEvidence, 'evidence_type' | 'relation' | 'snapshot'>, checkIn: string | null,
): EvidenceGroupId {
  const beforeOrAfter = (day: string | null): EvidenceGroupId =>
    (day && checkIn && day <= checkIn ? 'before_check_in' : 'after_checkout');

  switch (item.evidence_type) {
    case 'turnover_media':
      if (item.relation === 'before_check_in' || item.relation === 'after_checkout') return item.relation;
      return beforeOrAfter(dateOnly(snapshotString(item.snapshot, 'turnover_checkout')));
    case 'inspection_photo':
      return beforeOrAfter(dateOnly(snapshotString(item.snapshot, 'inspection_date')));
    case 'checkout_check':
      return 'checkout_checks';
    case 'lock_event':
    case 'noise_event':
    case 'checkin_agreement':
      return 'logs';
    case 'airbnb_thread':
    case 'vrbo_interaction':
    case 'booking_com_interaction':
      return 'messages';
  }
}

/** The case's evidence by group, each group keeping the server's order (oldest recorded first). */
export function groupEvidence(
  evidence: readonly CaseEvidence[], checkIn: string | null,
): Record<EvidenceGroupId, CaseEvidence[]> {
  const groups: Record<EvidenceGroupId, CaseEvidence[]> = {
    before_check_in: [], after_checkout: [], checkout_checks: [], logs: [], messages: [],
  };
  for (const item of evidence) groups[evidenceGroupOf(item, checkIn)].push(item);
  return groups;
}

export function isMediaEvidence(type: EvidenceType): boolean {
  return type === 'turnover_media' || type === 'inspection_photo';
}

/** Alt text for a photo: what it is and, when the uploader wrote one, its caption. */
export function evidenceAltText(item: Pick<CaseEvidence, 'label' | 'snapshot' | 'evidence_type'>): string {
  const label = item.label ?? EVIDENCE_TYPE_LABELS[item.evidence_type];
  const caption = snapshotString(item.snapshot, 'caption');
  return caption && !label.includes(caption) ? `${label}: ${caption}` : label;
}

// ─── Stay changes ─────────────────────────────────────────────────────────────

export interface StayChangeLine {
  id: string;
  text: string;
  /** Who made the change and the note they left, when recorded. */
  detail: string | null;
}

const asDay = (value: unknown): string | null =>
  (typeof value === 'string' && DATE_ONLY.test(value) ? value : null);

function changeDetail(entry: StayHistoryEntry): string | null {
  const parts = [
    entry.changed_by_name ? `Changed by ${entry.changed_by_name}` : null,
    entry.note ? `Note: ${entry.note}` : null,
  ].filter((part): part is string => part !== null);
  return parts.length > 0 ? parts.join('. ') : null;
}

function onDay(changedAt: string | null, now: Date): string {
  const date = parseServerTime(changedAt);
  return date ? ` on ${formatDayOfInstant(date, now)}` : '';
}

function describeDateChange(before: { start: string; end: string }, after: { start: string; end: string }): string {
  const nightsBefore = nightsBetween(before.start, before.end);
  const nightsAfter = nightsBetween(after.start, after.end);
  if (nightsAfter < nightsBefore) return `Shortened from ${formatNights(nightsBefore)} to ${nightsAfter}`;
  if (nightsAfter > nightsBefore) return `Extended from ${formatNights(nightsBefore)} to ${nightsAfter}`;
  return `Moved from ${formatStayRange(before.start, before.end)} to ${formatStayRange(after.start, after.end)}`;
}

/**
 * What happened to the stay, oldest first: date changes worded as
 * shortened, extended or moved ("Shortened from 2 nights to 1 on Sep 18"),
 * and cancellations. A history entry records only the dates it changed, so
 * each change's full before and after is rebuilt by walking back from the
 * stay's current dates.
 */
export function stayChangeLines(detail: GuestCaseDetail, now: Date = new Date()): StayChangeLine[] {
  const { stay, stay_history: history } = detail;
  if (!stay) {
    return detail.case.calendar_event_id === null
      ? [{
        id: 'stay-deleted',
        text: 'This stay is no longer on your calendar. The case keeps the dates it had when you opened it.',
        detail: null,
      }]
      : [];
  }

  const spans = new Map<number, { before: { start: string; end: string }; after: { start: string; end: string } }>();
  let current = { start: stay.check_in, end: stay.check_out };
  for (const entry of [...history].reverse()) {
    if (!entry.start_date && !entry.end_date) continue;
    const after = {
      start: asDay(entry.start_date?.new) ?? current.start,
      end: asDay(entry.end_date?.new) ?? current.end,
    };
    const before = {
      start: asDay(entry.start_date?.old) ?? after.start,
      end: asDay(entry.end_date?.old) ?? after.end,
    };
    spans.set(entry.id, { before, after });
    current = before;
  }

  const lines: StayChangeLine[] = [];
  for (const entry of history) {
    const span = spans.get(entry.id);
    if (span && (span.before.start !== span.after.start || span.before.end !== span.after.end)) {
      lines.push({
        id: `dates-${entry.id}`,
        text: `${describeDateChange(span.before, span.after)}${onDay(entry.changed_at, now)}`,
        detail: changeDetail(entry),
      });
    } else if (entry.new_status === CANCELLED_STATUS && entry.old_status !== CANCELLED_STATUS) {
      lines.push({ id: `cancel-${entry.id}`, text: `Cancelled${onDay(entry.changed_at, now)}`, detail: changeDetail(entry) });
    }
  }

  if (stay.booking_status === CANCELLED_STATUS && !lines.some((line) => line.id.startsWith('cancel-'))) {
    lines.push({ id: 'cancelled-now', text: 'This stay is marked cancelled.', detail: null });
  }
  return lines;
}
